import type { PayrollBankAccountOption, PayrollCalculatorEditableRow, PayrollCalculatorRow } from '@/lib/api/accounting';
import { evaluatePayrollFormula, resolveRowNet, type PayrollNetMode } from '@/lib/payroll/payrollFormulas';

export function defaultOvertimeHourlyRate(dailySalary: number): number {
  if (dailySalary <= 0) return 0;
  return Math.round(dailySalary / 8);
}

function bankFieldsFromSelection(
  accounts: PayrollBankAccountOption[],
  selectedId: number | null,
): Pick<PayrollCalculatorEditableRow, 'bank_name' | 'account_number' | 'iban' | 'selected_bank_account_id'> {
  const selected = accounts.find((a) => a.id === selectedId) ?? accounts[0];
  if (!selected) {
    return { selected_bank_account_id: null, bank_name: null, account_number: null, iban: null };
  }
  return {
    selected_bank_account_id: selected.id,
    bank_name: selected.bank_name,
    account_number: selected.account_number ?? null,
    iban: selected.iban ?? null,
  };
}

export function toEditableRows(rows: PayrollCalculatorRow[]): PayrollCalculatorEditableRow[] {
  return rows.map((row) => {
    const overtime_hourly_rate = defaultOvertimeHourlyRate(row.daily_salary);
    const manual_advance_deduction = Number(row.advance_deduction || 0);
    const manual_incentive = 0;
    const bank_accounts = row.bank_accounts ?? [];
    const selectedId = row.selected_bank_account_id ?? bank_accounts.find((b) => b.is_default)?.id ?? bank_accounts[0]?.id ?? null;
    const bankFields = bankFieldsFromSelection(bank_accounts, selectedId);
    const draft: PayrollCalculatorEditableRow = {
      ...row,
      ...bankFields,
      bank_accounts,
      overtime_hourly_rate,
      manual_advance_deduction,
      manual_incentive,
      net_mode: 'auto',
      net_formula: null,
      computed_net: 0,
      excluded: false,
    };
    draft.computed_net = resolveRowNet(draft);
    return draft;
  });
}

export function computeNet(
  row: Pick<PayrollCalculatorRow, 'worked_salary' | 'absence_deduction' | 'overtime_hours'>,
  overtimeHourlyRate: number,
  advanceDeduction: number,
  incentive: number,
): number {
  const overtimePay = Math.round(Number(row.overtime_hours || 0) * Number(overtimeHourlyRate || 0));
  const gross = Number(row.worked_salary || 0) + overtimePay + Number(incentive || 0);
  return Math.round(gross - Number(row.absence_deduction || 0) - Number(advanceDeduction || 0));
}

export type PayrollRowPatch = Partial<
  Pick<
    PayrollCalculatorEditableRow,
    | 'base_salary'
    | 'daily_salary'
    | 'worked_salary'
    | 'absence_deduction'
    | 'overtime_hourly_rate'
    | 'manual_advance_deduction'
    | 'manual_incentive'
    | 'computed_net'
    | 'net_mode'
    | 'net_formula'
    | 'selected_bank_account_id'
    | 'excluded'
  >
>;

export function isPayrollRowActive(row: PayrollCalculatorEditableRow): boolean {
  return !row.excluded;
}

export function filterActivePayrollRows(rows: PayrollCalculatorEditableRow[]): PayrollCalculatorEditableRow[] {
  return rows.filter(isPayrollRowActive);
}

export function setPayrollRowExcluded(
  rows: PayrollCalculatorEditableRow[],
  rowKey: string,
  excluded: boolean,
): PayrollCalculatorEditableRow[] {
  return rows.map((r) => (r.row_key === rowKey ? { ...r, excluded } : r));
}

export function setPayrollEmployeeExcluded(
  rows: PayrollCalculatorEditableRow[],
  employeeId: number,
  excluded: boolean,
): PayrollCalculatorEditableRow[] {
  return rows.map((r) => (r.employee_id === employeeId ? { ...r, excluded } : r));
}

function recalcDailyFromBase(row: PayrollCalculatorEditableRow): PayrollCalculatorEditableRow {
  if (row.salary_type !== 'monthly') return row;
  const days = Number(row.working_days_per_month || 30) || 30;
  return {
    ...row,
    daily_salary: days > 0 ? Math.round(Number(row.base_salary || 0) / days) : 0,
  };
}

function keepNetMode(
  row: PayrollCalculatorEditableRow,
  patch: PayrollRowPatch,
): PayrollCalculatorEditableRow['net_mode'] {
  if (patch.net_mode !== undefined) return patch.net_mode;
  if (patch.computed_net !== undefined) return 'manual';
  return row.net_mode;
}

function finalizeRow(row: PayrollCalculatorEditableRow): PayrollCalculatorEditableRow {
  const bankFields =
    row.selected_bank_account_id != null
      ? bankFieldsFromSelection(row.bank_accounts, row.selected_bank_account_id)
      : {};
  const merged = { ...row, ...bankFields };
  return { ...merged, computed_net: resolveRowNet(merged) };
}

export function patchEditableRow(row: PayrollCalculatorEditableRow, patch: PayrollRowPatch): PayrollCalculatorEditableRow {
  const net_mode = keepNetMode(row, patch);
  let next: PayrollCalculatorEditableRow = { ...row, ...patch, net_mode };

  if (patch.base_salary !== undefined) {
    next = recalcDailyFromBase(next);
  }

  if (patch.selected_bank_account_id !== undefined) {
    Object.assign(next, bankFieldsFromSelection(next.bank_accounts, next.selected_bank_account_id));
  }

  if (patch.net_mode === 'auto') {
    next.net_formula = null;
  }

  if (patch.net_mode === 'formula' && patch.net_formula) {
    next.net_formula = patch.net_formula;
  }

  return finalizeRow(next);
}

export function applyPayrollFormulaToRows(
  rows: PayrollCalculatorEditableRow[],
  formula: string,
  filter?: (row: PayrollCalculatorEditableRow) => boolean,
): PayrollCalculatorEditableRow[] {
  const trimmed = formula.trim();
  return rows.map((row) => {
    if (filter && !filter(row)) return row;
    try {
      const computed_net = evaluatePayrollFormula(trimmed, row);
      return finalizeRow({
        ...row,
        net_mode: 'formula' as PayrollNetMode,
        net_formula: trimmed,
        computed_net,
      });
    } catch {
      return row;
    }
  });
}

export function syncEmployeeBankSelection(
  rows: PayrollCalculatorEditableRow[],
  employeeId: number,
  bankAccountId: number | null,
): PayrollCalculatorEditableRow[] {
  return rows.map((row) =>
    row.employee_id === employeeId
      ? finalizeRow(patchEditableRow(row, { selected_bank_account_id: bankAccountId }))
      : row,
  );
}

export const PAYROLL_DRAFT_STORAGE_KEY = 'accounting_payroll_draft_v1';

export function savePayrollDraft(payload: unknown): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PAYROLL_DRAFT_STORAGE_KEY, JSON.stringify(payload));
}

export function readPayrollDraft<T>(): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PAYROLL_DRAFT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function clearPayrollDraft(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PAYROLL_DRAFT_STORAGE_KEY);
}

export type PayrollProjectGroup = {
  project_id: number;
  project_name: string;
  rows: PayrollCalculatorEditableRow[];
  totalNet: number;
  employeeCount: number;
};

export function groupRowsByProject(rows: PayrollCalculatorEditableRow[]): PayrollProjectGroup[] {
  const map = new Map<number, PayrollProjectGroup & { activeEmployeeIds: Set<number> }>();
  for (const row of rows) {
    const prev = map.get(row.project_id);
    if (!prev) {
      const activeEmployeeIds = new Set<number>();
      if (isPayrollRowActive(row)) activeEmployeeIds.add(row.employee_id);
      map.set(row.project_id, {
        project_id: row.project_id,
        project_name: row.project_name,
        rows: [row],
        totalNet: isPayrollRowActive(row) ? row.computed_net : 0,
        employeeCount: activeEmployeeIds.size,
        activeEmployeeIds,
      });
      continue;
    }
    prev.rows.push(row);
    if (isPayrollRowActive(row)) {
      prev.totalNet += row.computed_net;
      prev.activeEmployeeIds.add(row.employee_id);
      prev.employeeCount = prev.activeEmployeeIds.size;
    }
  }
  return [...map.values()]
    .map(({ activeEmployeeIds: _a, ...g }) => g)
    .sort((a, b) => a.project_name.localeCompare(b.project_name, 'fa'));
}

export type EmployeePayrollSummary = {
  employee_id: number;
  employee_name: string;
  employee_code?: string | null;
  project_parts: Array<{ project_id: number; project_name: string; net: number }>;
  total_net: number;
  bank_name?: string | null;
  account_number?: string | null;
  iban?: string | null;
  selected_bank_account_id?: number | null;
  bank_accounts: PayrollBankAccountOption[];
};

export function buildEmployeePayrollSummary(rows: PayrollCalculatorEditableRow[]): EmployeePayrollSummary[] {
  const map = new Map<number, EmployeePayrollSummary>();
  for (const row of filterActivePayrollRows(rows)) {
    const prev = map.get(row.employee_id);
    const part = { project_id: row.project_id, project_name: row.project_name, net: row.computed_net };
    if (!prev) {
      map.set(row.employee_id, {
        employee_id: row.employee_id,
        employee_name: row.employee_name,
        employee_code: row.employee_code,
        project_parts: [part],
        total_net: row.computed_net,
        bank_name: row.bank_name,
        account_number: row.account_number,
        iban: row.iban,
        selected_bank_account_id: row.selected_bank_account_id,
        bank_accounts: row.bank_accounts,
      });
      continue;
    }
    prev.project_parts.push(part);
    prev.total_net += row.computed_net;
    if (row.selected_bank_account_id) {
      prev.selected_bank_account_id = row.selected_bank_account_id;
      prev.bank_name = row.bank_name;
      prev.account_number = row.account_number;
      prev.iban = row.iban;
    }
    if (row.bank_accounts.length > prev.bank_accounts.length) {
      prev.bank_accounts = row.bank_accounts;
    }
  }
  return [...map.values()].sort((a, b) => a.employee_name.localeCompare(b.employee_name, 'fa'));
}

export function netModeLabel(mode: PayrollNetMode): string {
  if (mode === 'manual') return 'دستی';
  if (mode === 'formula') return 'فرمول';
  return 'خودکار';
}
